import { useQuery } from '@apollo/client';
import { BlockedPhoneNumber } from '@provision/entity-types';
import { QueryHookOptions } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { GET_BLOCKED_PHONE_NUMBER_QUERY } from 'src/graphql/entities/blocked-phone-number/blocked-phone-number.operations';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

export type GetBlockedPhoneNumberQuery = {
  getBlockedPhoneNumber: Records<BlockedPhoneNumber>;
};

export type GetBlockedPhoneNumberQueryParams = {
  filters?: ConditionInput[];
  sorting?: SortingInput[];
  limit?: number;
  offset?: number;
};

export const useBlockedPhoneNumber = (
  params: GetBlockedPhoneNumberQueryParams = {},
  options: QueryHookOptions<GetBlockedPhoneNumberQuery> = {},
) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const filters = useMemo(() => {
    const partnerIds = [PLATFORM_PARTNER_ID];
    if (selectedPartnerId) {
      partnerIds.push(selectedPartnerId);
    }
    const partnerFilter: ConditionInput = {
      attribute: 'partnerId',
      operator: 'in',
      value: partnerIds.join(','),
    };
    return [
      partnerFilter,
      // apply all other filters provided by caller (except filtering by partnerId).
      ...(params.filters || []).filter((f) => f.attribute !== 'partnerId'),
    ];
  }, [selectedPartnerId, params.filters]);

  const {
    data,
    loading: isLoading,
    fetchMore,
    variables,
  } = useQuery<GetBlockedPhoneNumberQuery, QueryVariables>(GET_BLOCKED_PHONE_NUMBER_QUERY, {
    ...options,
    variables: {
      params: { ...params, filters },
    },
    skip: !filters.length,
    context: {
      includePlatformLevelData: true,
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to query blocked phone numbers, please try again');
    },
  });

  const records = useMemo(
    () => data?.getBlockedPhoneNumber.records || [],
    [data?.getBlockedPhoneNumber.records],
  );

  const totalRecords = data?.getBlockedPhoneNumber?.metadata?.totalRecords ?? 0;

  const { loadMore, hasReachedEnd } = useLoadMore<GetBlockedPhoneNumberQuery>({
    offset: records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  return {
    blockedPhoneNumbers: records,
    isLoading,
    loadMore,
    totalRecords,
    hasMore: totalRecords ? records?.length < totalRecords : !hasReachedEnd,
  };
};
